import React, {Fragment} from 'react';

import {Stack} from '@mui/material';
import {parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {AppointmentBookingEnum, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';

import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {translations} from 'presentation/translations-new';
import {Drawer} from '../../../../../components/Drawer';
import {Filter} from '../../../../../components/Filter';
import {FlowEnum} from '../../constants';
import {useSearchBar} from '../../hooks/useSearchBar/useSearchBar';
import {CurrentLocation} from '../CurrentLocation';
import {FilterByLetter} from '../FilterByLetter';

import {SearchBarProps} from './types';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';
import {useDistanceSliderStore} from 'presentation/modules/booking/shared/ui/components/DistanceSlider/store';
import {UnitsFilterDrawer} from 'presentation/modules/booking/shared/ui/components/UnitsFilterDrawer';

const {apply, nearestDates, filter, distance} = translations['pt-br'].commons;

export function SearchBar({
	placeholder,
	readOnly,
	activeStep,
	flowType,
	searchDataTagId,
}: SearchBarProps) {
	const {
		openDrawerState,
		handleOpenDrawer,
		handleCloseDrawer,
		disabledSelectionDate,
		showProfessionalFilters,
		showFilterByLetter,
		availableDatesList,
		handleChangeDate,
		PERIODS,
		handleConfirmShift,
		handleClearShift,
		dateAt,
		shift,
		showFilter,
		filterIsActive,
		showDistanceDrawer,
		getProviderNamesState,
		IS_PROVIDER_NAMES_ERROR,
		IS_PROVIDER_NAMES_SUBMITTING,
		IS_PROVIDER_NAMES_DONE,
		isTelehealth,
		isHealth,
		showOdontoDrawer,
		showGeolocationFilterDrawer,
		SERVING_PEOPLE_WITH_DISABILITIES,
		handleClearDisability,
		handleConfirmDisability,
		distanceFilterAtMaxDistance,
		handleConfirmDistance,
		handleClearDistance,
		districtGeolocationState,
		setDistrictGeolocationState,
		selectedFilterGeolocationState,
		setSelectedFilterGeolocationState,
		isFavoriteDoctorsFlow,
	} = useSearchBar({activeStep, flowType});

	const externalOpenDateModal = useExternalOpenDateModalStore(
		state => state.externalOpenDateModal,
	);
	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const bookingData = useBookingStore(state => state.bookingData);
	const {isManualLocation} = bookingData;

	const isHealthOrExamOrOdonto =
		flowType === FlowEnum.HEALTH || flowType === FlowEnum.EXAM || flowType === FlowEnum.ODONTO;
	const isOdonto = flowType === FlowEnum.ODONTO;

	const currentSelectedDate = dateAt ? parse(dateAt, 'P', 0, {locale: ptBR}) : undefined;

	const availableDatesLoading = availableDatesList.state === 'loading';
	const availableDatesError = availableDatesList.state === 'hasError';
	const availableDatesLoadingOrError = availableDatesLoading || availableDatesError;
	const availableDates = availableDatesLoadingOrError ? [] : availableDatesList.contents;

	const autocompleteRules = [
		{step: 1, condition: isTelehealth},
		{step: 3, condition: !isManualLocation && isHealth},
		{step: 4, condition: isManualLocation && isHealth},
	];

	const shouldRenderAutocomplete =
		autocompleteRules.some(rule => rule.step === activeStep && rule.condition) &&
		!IS_PROVIDER_NAMES_ERROR &&
		!!getProviderNamesState;

	const isGettingProviderNames =
		!IS_PROVIDER_NAMES_DONE || IS_PROVIDER_NAMES_SUBMITTING || availableDatesLoading;

	const {UNIT_STEP, PROFESSIONAL_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	const showCurrentLocation = isHealthOrExamOrOdonto && activeStep === UNIT_STEP;

	const distanceValue = useDistanceSliderStore(state => state.distanceSlider);
	const distanceLabel = `${distanceValue}km`;

	const filterCount = () => {
		if (isOdonto) {
			return filterIsActive ? 1 : undefined;
		}

		return filterIsActive ? distanceLabel : undefined;
	};

	const hideElementProfessionalStepIsFavoriteDoctors =
		isFavoriteDoctorsFlow &&
		activeStep === PROFESSIONAL_STEP &&
		(flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH);

	const hideElementUnitStepIsFavoriteDoctors =
		isFavoriteDoctorsFlow &&
		activeStep === UNIT_STEP &&
		(flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH);

	return (
		<Filter.Root>
			{hideElementProfessionalStepIsFavoriteDoctors ? null : (
				<Stack direction={'row'} gap={1}>
					{shouldRenderAutocomplete ? (
						<Filter.SearchWithAutocomplete
							placeholder={placeholder}
							loadingList={isGettingProviderNames}
							autocompleteConfig={{
								options:
									getProviderNamesState?.medicos?.map(medico => ({
										id: medico?.cdPrestadorFisico,
										label: medico?.nmMedico,
									})) ?? [],
							}}
						/>
					) : (
						<Filter.Search
							dataTagId={searchDataTagId}
							placeholder={placeholder}
							readOnly={readOnly}
						/>
					)}
					{showFilter && !hideElementUnitStepIsFavoriteDoctors ? (
						<Fragment>
							<Filter.Button
								onClick={handleOpenDrawer}
								count={filterCount()}
								customLabel={isOdonto ? undefined : distance}
								data-tag-id={
									showDistanceDrawer
										? AppointmentBookingEnum.ALTEROU_DISTANCIA_POR_FILTRO
										: OdontoBookingTaggingEnum.CLICK_FILTRAR_UNIDADES
								}
							/>

							<UnitsFilterDrawer
								flowType={flowType}
								openDrawerState={openDrawerState}
								handleOpenDrawer={handleOpenDrawer}
								handleCloseDrawer={handleCloseDrawer}
								showDistanceDrawer={showDistanceDrawer}
								showOdontoDrawer={showOdontoDrawer}
								showGeolocationFilterDrawer={showGeolocationFilterDrawer}
								servingPeopleWithDisabilities={SERVING_PEOPLE_WITH_DISABILITIES}
								handleClearDisability={handleClearDisability}
								handleConfirmDisability={handleConfirmDisability}
								distanceFilterAtMaxDistance={distanceFilterAtMaxDistance}
								handleConfirmDistance={handleConfirmDistance}
								handleClearDistance={handleClearDistance}
								districtGeolocationState={districtGeolocationState}
								setDistrictGeolocationState={setDistrictGeolocationState}
								selectedFilterGeolocationState={selectedFilterGeolocationState}
								setSelectedFilterGeolocationState={
									setSelectedFilterGeolocationState
								}
							/>
						</Fragment>
					) : null}
				</Stack>
			)}
			{showFilterByLetter ? <FilterByLetter flowType={flowType} /> : null}
			{showProfessionalFilters ? (
				<Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
					<Filter.Date
						dataTagId={{
							openButton: AppointmentBookingEnum.CLICK_DATAS_MAIS_PROXIMAS,
						}}
						currentFirstDate={currentSelectedDate}
						selectFirstDate={handleChangeDate}
						customOpenModalButton={nearestDates}
						customLabelConfirmButton={apply}
						availableDates={availableDates}
						disableConfirmButton={disabledSelectionDate}
						externalOpen={externalOpenDateModal}
						onAbandonModal={() => setExternalOpenDateModal(false)}
					/>
					<Filter.Button onClick={handleOpenDrawer} count={shift ? 1 : undefined} />
					<Drawer.Root
						open={openDrawerState}
						onOpen={handleOpenDrawer}
						onClose={() => handleCloseDrawer(true)}
						width={'420px'}
						sx={{zIndex: 9000}}>
						<Drawer.Header title={filter} onClose={() => handleCloseDrawer(true)} />
						<Drawer.ContentListByCategory categoryList={[PERIODS]} />
						<Drawer.Footer
							firstButtonOnClick={handleClearShift}
							secondButtonOnClick={handleConfirmShift}
						/>
					</Drawer.Root>
				</Stack>
			) : null}
			{showCurrentLocation && !hideElementUnitStepIsFavoriteDoctors ? (
				<CurrentLocation
					dataTagId={AppointmentBookingEnum.CLICK_ALTERAR_LOCALIZACAO}
					flowType={flowType}
				/>
			) : null}
		</Filter.Root>
	);
}
